/**
 * One re-INVITE at a time per call (spec §10.5).
 *
 * Every re-INVITE a call sends goes through its queue: turning the camera on or off, hold and unhold, and SipClient's
 * ICE-restart renegotiation. Two of ours must never overlap - JsSIP refuses a second offer while one is open - and none
 * may go out right after answer, when Asterisk re-INVITEs the call itself and a collision loops on 491.
 *
 * - **Settling:** nothing is sent until `settleMs` after answer, and then only while JsSIP says the session is ready to
 *   re-offer (no other INVITE or UPDATE transaction open). Readiness is polled every 200 ms.
 * - **Merging:** renegotiation requests made while no re-INVITE of theirs has gone out yet share one. A request made
 *   while one is in flight waits for it and goes out in the next, together with every other request made meanwhile.
 *   Each caller gets the result of the re-INVITE that carried its intent. Since each re-INVITE offers the connection's
 *   state as it is when sent, the last camera click always wins.
 * - **Hold and unhold** are jobs of their own, in order with the renegotiations. The queue keeps the hold state last
 *   asked for, so a second hold is recognised as redundant, and a hold or unhold that a later one overtook says so -
 *   the later one decides what the call ends up as.
 * - **Failures keep the call:** every JsSIP call passes `terminateOnFailure: false` (JsSIP fork 4.13.0), so a failed
 *   re-INVITE is rolled back and reported through `done(err)` instead of ending the call. A refusal - a 4xx such as
 *   488, a 6xx, or an answer that can't be applied - resolves false. A passing failure - a 491 that JsSIP's retry
 *   didn't resolve, a 5xx, or a failure on our side before any answer - rejects: it says nothing about whether the call
 *   can carry what was offered.
 * - **The call ending** (the session's 'ended' or 'failed', or close()) settles everything still open as false. JsSIP
 *   ends the call without calling done on a request timeout, a transport error or a 408/481, so this is what releases
 *   anyone waiting then.
 * - **A re-INVITE from the other side while an offer of ours is out** gets 491 (RFC 3261 14.2), as JsSIP does not. JsSIP
 *   would apply it on top of our offer: the browser rolls ours back, and our own re-INVITE then meets an answer it can't
 *   apply, or a 491, while the camera transceiver has lost its m-line. A guest's camera-on, relayed by the PBX, crosses
 *   the host's at answer that way (2026-10-12). The other side retries after its 491, as we do after ours; while JsSIP
 *   waits to retry ours, our offer is rolled back, so theirs gets in.
 */

import logger from '@/utilities/Logger'

/** JsSIP's completion callback: no argument on success, the error on failure */
type Done = (err?: unknown) => void

/** The part of JsSIP's 'sdp' event the queue looks at */
interface SdpEvent {
	originator?: unknown
	type?: unknown
}

/** The part of JsSIP's 'reinvite' event the queue uses: reject() answers it with a final response instead of the SDP */
interface ReinviteEvent {
	reject: (options: { status_code: number, reason_phrase?: string }) => unknown
}

/** The subset of a JsSIP RTCSession the queue drives */
export interface QueueSession {
	isReadyToReOffer(): boolean
	/** @returns false when the re-INVITE could not be started */
	renegotiate(o: object, done: Done): boolean
	hold(o: object, done: Done): boolean
	unhold(o: object, done: Done): boolean
	isOnHold(): { local: boolean }
	/** The call's peer connection: 'have-local-offer' while an offer of ours is out */
	readonly connection?: { signalingState: RTCSignalingState }
	/** 'ended' and 'failed' settle the queue; 'sdp' tells whether a failed re-INVITE got as far as an answer */
	on(event: 'ended' | 'failed' | 'sdp', listener: (event?: SdpEvent) => void): unknown
	/** A re-INVITE from the other side, before JsSIP applies it */
	on(event: 'reinvite', listener: (event: ReinviteEvent) => void): unknown
}

export interface RenegotiationQueueOptions {
	/** When the call was answered, as a now() time - undefined while it isn't */
	answeredAt: () => number | undefined
	/** How long after answer before the first re-INVITE */
	settleMs: number
	now: () => number
	setTimer: (fn: () => void, ms: number) => unknown
	/** Cancels a timer from setTimer, when the call ends */
	clearTimer?: (handle: unknown) => void
}

export interface RenegotiationRequest {
	/** Passed to JsSIP's renegotiate - merged with those of the requests sharing the re-INVITE */
	rtcOfferConstraints?: object
}

/** What became of a hold or unhold */
export type HoldResult =
	/** The call is now held (unheld) */
	| 'done'
	/** The re-INVITE failed and JsSIP put its hold state back, or the call ended */
	| 'failed'
	/** The call already was, or was already going to be, held (unheld) - nothing sent */
	| 'redundant'
	/** A later hold or unhold was asked for before this one finished - that one decides */
	| 'overtaken'

function errorText(err: unknown): string {
	return err instanceof Error ? err.message : String(err)
}

/**
 * Whether a failed re-INVITE says nothing about what the call can carry: glare JsSIP's retry didn't resolve (491), a
 * server error (5xx), or a failure on our side before any answer came
 * @param err The error JsSIP passed to done() - the fork puts the response's status_code on it
 * @param answered Whether an answer to the re-INVITE arrived - then it is that answer that failed
 */
function isPassingFailure(err: unknown, answered: boolean): boolean {
	const status = (err as { status_code?: unknown } | undefined)?.status_code

	if (typeof status === 'number') { return status === 491 || (status >= 500 && status < 600) }
	return !answered
}

/** How often to check again whether the session is ready to re-offer */
const POLL_MS = 200

/** Keep the call when the re-INVITE fails - typed in @per_moeller/jssip-telecomx 4.13.0, see queueSession() in SipClient */
const KEEP_CALL_ON_FAILURE = { terminateOnFailure: false } as const

type JobKind = 'renegotiate' | 'hold' | 'unhold'

/** How a job ended: a result for its callers, or an error to reject them with */
type Outcome = { ok: boolean } | { error: unknown }

interface Waiter {
	resolve: (result: boolean) => void
	reject: (err: unknown) => void
}

interface Job {
	kind: JobKind
	rtcOfferConstraints?: object
	waiters: Waiter[]
	/** Handed to JsSIP - later requests can no longer ride along */
	sent: boolean
	/** An answer to its re-INVITE arrived */
	answered: boolean
	settled: boolean
}

export class RenegotiationQueue {
	private readonly session: QueueSession

	private readonly options: RenegotiationQueueOptions

	/** Oldest first; the first one is the one being sent or waiting to be sent */
	private readonly jobs: Job[] = []

	/** The first job is being worked on - waiting, or in flight */
	private busy = false

	private closed = false

	/** The timer the first job is waiting on, if any */
	private timer: unknown = undefined

	/** The hold state last asked for, undefined until the first hold or unhold */
	private wantHeld?: boolean

	/** Bumped by every hold and unhold asked for, to tell an overtaken one */
	private holdGeneration = 0

	constructor(session: QueueSession, options: RenegotiationQueueOptions) {
		this.session = session
		this.options = options

		session.on('ended', () => { this.close() })
		session.on('failed', () => { this.close() })
		session.on('sdp', event => {
			const job = this.jobs[0]

			if (job?.sent && event?.originator === 'remote' && event.type === 'answer') { job.answered = true }
		})
		session.on('reinvite', event => {
			if (session.connection?.signalingState !== 'have-local-offer') { return }
			logger.info('RenegotiationQueue: A re-INVITE from the other side crosses ours - answering 491')
			event.reject({ status_code: 491, reason_phrase: 'Request Pending' })
		})
	}

	/** Whether the call is held or a hold was asked for since - what the next hold or unhold is measured against */
	get holdWanted(): boolean {
		return this.wantHeld ?? this.session.isOnHold().local
	}

	/**
	 * Ask for a re-INVITE offering the call's current media state
	 * @param request Options for the re-INVITE
	 * @returns Resolves true for a 2xx, false when it was refused or the call ended; rejects for a passing failure
	 * (491, 5xx, nothing sent or answered) or when JsSIP threw
	 */
	request(request: RenegotiationRequest = {}): Promise<boolean> {
		return this.enqueue('renegotiate', request.rtcOfferConstraints)
	}

	/**
	 * Put the call on hold, in turn with the re-INVITEs before it
	 * @returns What became of it - never rejects
	 */
	hold(): Promise<HoldResult> {
		return this.changeHold(true)
	}

	/**
	 * Take the call off hold, in turn with the re-INVITEs before it
	 * @returns What became of it - never rejects
	 */
	unhold(): Promise<HoldResult> {
		return this.changeHold(false)
	}

	/** The call has ended: settle everything still open as false and never send again */
	close(): void {
		if (this.closed) { return }
		this.closed = true

		if (this.timer !== undefined) { this.options.clearTimer?.(this.timer) }
		this.timer = undefined

		for (const job of this.jobs.splice(0)) { this.settle(job, { ok: false }) }
	}

	private async changeHold(held: boolean): Promise<HoldResult> {
		if (this.closed) { return 'failed' }
		if (this.holdWanted === held) { return 'redundant' }

		this.wantHeld = held

		const generation = ++this.holdGeneration
		// Queued before the first await, so the order of holds and unholds is the order they were asked for
		const job = this.enqueue(held ? 'hold' : 'unhold')
		const ok = await job.catch(() => false)

		if (generation !== this.holdGeneration) { return 'overtaken' }
		if (!ok) {
			// JsSIP put its own hold state back
			this.wantHeld = !held
			return 'failed'
		}
		return 'done'
	}

	private enqueue(kind: JobKind, rtcOfferConstraints?: object): Promise<boolean> {
		return new Promise<boolean>((resolve, reject) => {
			if (this.closed) {
				resolve(false)
				return
			}

			// Ride along with the last job if it is the same kind and still waiting to be sent
			let job = this.jobs.at(-1)

			if (job?.kind !== kind || job.sent) {
				job = { kind, waiters: [], sent: false, answered: false, settled: false }
				this.jobs.push(job)
			}
			if (rtcOfferConstraints) { job.rtcOfferConstraints = { ...job.rtcOfferConstraints, ...rtcOfferConstraints } }
			job.waiters.push({ resolve, reject })
			this.next()
		})
	}

	/** Start on the first job, unless one is being worked on */
	private next() {
		const job = this.jobs[0]

		if (this.closed || this.busy || !job) { return }
		this.busy = true
		this.attempt(job)
	}

	/** Send the job if the call has settled and the session is ready, otherwise look again later */
	private attempt(job: Job) {
		if (job.settled) { return }

		const answeredAt = this.options.answeredAt()

		if (answeredAt === undefined) {
			this.retry(job, POLL_MS)
			return
		}

		const settling = answeredAt + this.options.settleMs - this.options.now()

		if (settling > 0) {
			this.retry(job, settling)
			return
		}
		if (!this.session.isReadyToReOffer()) {
			this.retry(job, POLL_MS)
			return
		}

		// Already where the hold or unhold would take the call, e.g. an unhold after a hold that failed
		if (job.kind !== 'renegotiate' && this.session.isOnHold().local === (job.kind === 'hold')) {
			this.finish(job, { ok: true })
			return
		}

		job.sent = true

		let started: boolean

		try {
			started = this.send(job, err => {
				if (err === undefined) {
					this.finish(job, { ok: true })
					return
				}

				const passing = isPassingFailure(err, job.answered)

				logger.warning(`RenegotiationQueue: The ${job.kind} re-INVITE failed (${passing ? 'passing' : 'refused'}) - ${errorText(err)}`)
				this.finish(job, passing ? { error: err } : { ok: false })
			})
		}
		catch (err) {
			logger.warning(`RenegotiationQueue: JsSIP threw on ${job.kind} - ${errorText(err)}`)
			this.finish(job, { error: err })
			return
		}

		// JsSIP found the session busy after all - requests made meanwhile can still ride along
		if (!started && !job.settled) {
			job.sent = false
			this.retry(job, POLL_MS)
		}
	}

	private send(job: Job, done: Done): boolean {
		switch (job.kind) {
			case 'hold': return this.session.hold({ ...KEEP_CALL_ON_FAILURE }, done)
			case 'unhold': return this.session.unhold({ ...KEEP_CALL_ON_FAILURE }, done)
			default: return this.session.renegotiate({ ...KEEP_CALL_ON_FAILURE, ...job.rtcOfferConstraints ? { rtcOfferConstraints: job.rtcOfferConstraints } : {} }, done)
		}
	}

	private retry(job: Job, ms: number) {
		this.timer = this.options.setTimer(() => {
			this.timer = undefined
			if (!this.closed) { this.attempt(job) }
		}, ms)
	}

	/** The first job is done: tell its callers and move on to the next */
	private finish(job: Job, outcome: Outcome) {
		if (job.settled || this.jobs[0] !== job) { return }

		this.jobs.shift()
		this.busy = false
		this.settle(job, outcome)
		this.next()
	}

	private settle(job: Job, outcome: Outcome) {
		if (job.settled) { return }
		job.settled = true

		for (const waiter of job.waiters) {
			if ('error' in outcome) {
				waiter.reject(outcome.error)
			} else {
				waiter.resolve(outcome.ok)
			}
		}
	}
}
